import React, {useEffect} from 'react';
import h from 'react-hyperscript';
import {Button, Tooltip} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {ENDPOINTS, getAccessToken} from 'Src/utils/utils';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import ChipInput from 'Components/ChipInput/ChipInput';
import './MemberList.scss';

export default function MemberList(props) {
  const [loading, setLoading] = React.useState(false);
  const [newMember, setNewMember] = React.useState('');
  const [emails, setEmails] = React.useState([]);
  const [error, setError] = React.useState(false);
  const [valid, setValid] = React.useState(false);
  const [message, setMessage] = React.useState('You can add multiple ' +
    'email addresses at a time by using commas as the separator');


  const handleChange = (evt) => {
    setNewMember(sanitizeEmail(evt.target.value));
  };

  const handleDelete = (item) => {
    setEmails(
        emails.filter((i) => i !== item)
    );
  };

  const handleKeyDown = (evt) => {
    if (['Enter'].includes(evt.key)) {
      evt.preventDefault();

      if (valid) {
        addMembers();
      }
    }
  };

  const handlePaste = (evt) => {
    evt.preventDefault();
    const paste = evt.clipboardData.getData('text');
    const pastedEmails = paste.split(',').map((e) => {
      return sanitizeEmail(e);
    });

    const uniqueEmails = [...new Set(pastedEmails)];

    const emailsObjs = [];

    uniqueEmails.forEach((email) => {
      const obj = {};
      obj.value = email;
      if (isInList(email)) {
        return;
      }

      if (isEmail(email) && !isOwnEmail(email)) {
        obj.error = false;
      } else {
        obj.error = true;
      }
      emailsObjs.push(obj);
    });

    if (emailsObjs.length > 0) {
      setNewMember('');
      setEmails([...emails, ...emailsObjs]);
    }
  };

  const isEmail = (email) => {
    return /^.+@[^\.].*\.[A-Za-z]{2,}$/.test(email);
  };

  const isOwnEmail = (email) => {
    return email === props.userStatus.email;
  };

  const sanitizeEmail = (email) => {
    return email.toLowerCase();
  };

  const isInList = (email) => {
    const foundEmail = emails.find((e) => e.value === email);
    const foundEmailFromMembers = props.members.find((e) => {
      return e === email;
    });
    return !!foundEmail || !!foundEmailFromMembers;
  };


  const validation = () => {
    setError(false);
    setValid(true);
    setMessage('You can add multiple ' +
    'email addresses at a time by using commas as the separator');
    for (const item of emails) {
      if (item.error) {
        setError(true);
        setValid(false);
        if (!isEmail(item.value)) {
          setMessage(`${item.value} is an invalid address`);
        } else if (isOwnEmail(item.value)) {
          setMessage( 'Can\'t add own email');
        } else {
          setMessage(`${item.value} has already been added.`);
        }
      }
    }

    if (newMember !== '') {
      if (!isEmail(newMember)) {
        setError(true);
        setValid(false);
        setMessage(`${newMember} is an invalid address`);
      }

      if (isOwnEmail(newMember)) {
        setError(true);
        setValid(false);
        setMessage( 'Can\'t add own email');
      }

      if (isInList(newMember)) {
        setError(true);
        setValid(false);
        setMessage(`${newMember} has already been added.`);
      }
    }

    if (emails.length === 0 && newMember === '') {
      setValid(false);
    }
  };


  useEffect(validation, [newMember, emails]);

  const updateMembersList = (actionType, emailsList) => {
    return fetch(ENDPOINTS.UPDATE_MEMBERS_LIST, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': props.idToken,
        'X-CSRF-Token': props.csrf,
      },
      body: JSON.stringify({
        access_token: getAccessToken(props.googleUser),
        district_id: props.districtId,
        action: actionType,
        emails: emailsList,
      }),
    })
        .then((response) => {
          return response.json();
        })
        .then(
            (result) => {
              props.setMembers(result.emails);
            },
            (error) => {
              console.log('No result', error);
              setError(true);
              setMessage('Error adding email');
            }
        );
  };


  const addMembers = () => {
    const sanitizedEmails = newMember.trim().split(',');
    emails.forEach((e) => {
      sanitizedEmails.push(e.value.trim());
    });
    if (sanitizedEmails.length > 0) {
      setLoading(true);
      updateMembersList('add', sanitizedEmails)
          .then((res) => {
            setNewMember('');
            setEmails([]);
            setLoading(false);
            if (props.callback) {
              props.callback();
            }
          });
    } else {
      setValidation({
        error: true,
        message: 'Please enter each email followed by ' +
        '"enter"/"return" to add to the list, then try again.',
      });
    }
  };

  const removeMember = (email) => {
    const memberDiv = document.getElementById(email);
    memberDiv.classList.add('disabled');
    updateMembersList('remove', [email]);
  };

  return (
    h('div', {className: 'member-list__container'}, [
      h('div', {className: 'member-list__add'}, [
        h(ChipInput, {
          value: newMember,
          elements: emails,
          handleChange,
          handleKeyDown,
          handleDelete,
          handlePaste,
          error,
          message,
        }),
      loading ? h(LoadingIndicator, null, null) :
        h(Button, {
          color: 'primary',
          variant: 'contained',
          disabled: !valid,
          size: 'medium',
          onClick: () => addMembers(),
          color: 'primary',
          className: 'member-list__add-button',
        }, ['Add']),
      ]),
      props.members && h('div', {className: 'member-list__members'}, [
        props.members.map((member) => {
          return member.length ?
            h('div', {
              className: 'member-list__row',
              id: member, key: member,
            }, [
              h('img', {
                className: 'member-list__person-icon',
                src: '/assets/icons/profile.svg',
              }),
              h('p', {className: 'member-list__email'}, member),
              h(
                  Tooltip,
                  {
                    title: 'Revoke access for ' + member,
                  },
                  [
                    h(CloseIcon, {
                      onClick: () => removeMember(member),
                      className: 'member-list__remove',
                    }),
                  ]),
            ]) : null;
        }),
      ]),
    ])
  );
}
